import { Controller } from "@hotwired/stimulus"
import Accordion from "flowbite/lib/esm/components/accordion"

let sequence = 0

// Flowbite accordion. Triggers, and any panel that may be rendered later, are
// Stimulus targets:
//
//   <div data-controller="accordion" data-accordion-active-classes-value="…">
//     <button data-accordion-target="trigger" aria-controls="panel" aria-expanded="false">
//       … <svg data-accordion-icon class="rotate-180">
//     </button>
//     <div id="panel" class="hidden">…</div>
//     <div id="late" data-accordion-target="panel" class="hidden">…</div>
//   </div>
//
// Flowbite's Accordion captures every panel at construction and dereferences
// them on each open/close, so the instance is rebuilt whenever a trigger or a
// panel target enters or leaves the DOM. A trigger whose panel isn't rendered
// yet is left out until it is — which means a panel that arrives late (lazy
// turbo-frame, stream) MUST carry data-accordion-target="panel"; without it
// nothing triggers the rebuild and its trigger stays inert. State lives in
// aria-expanded, which Flowbite keeps current, so a rebuild is faithful: every
// item is opened or closed to match its trigger, so a panel Turbo re-rendered
// in the wrong state is re-synced.
// Nested accordions each get their own controller; Stimulus scopes targets
// to the nearest one.
//
// data-accordion-target is only ever read by Stimulus here: Flowbite's own
// scanner (initAccordions, in its entry points) is never loaded — only the
// Accordion class is imported — so the name cannot be interpreted twice.
export default class extends Controller {
  static targets = ["trigger", "panel"]
  static values = {
    activeClasses: String,
    inactiveClasses: String,
    alwaysOpen: Boolean,
  }

  // No build in connect(): Stimulus reports the initial targets just before
  // calling connect(), and those callbacks queue the first build.
  disconnect() {
    this._destroy()
  }

  triggerTargetConnected() {
    this._scheduleRebuild()
  }

  triggerTargetDisconnected() {
    this._scheduleRebuild()
  }

  panelTargetConnected() {
    this._scheduleRebuild()
  }

  panelTargetDisconnected() {
    this._scheduleRebuild()
  }

  // Target callbacks arrive one per element; coalesce them into one rebuild.
  _scheduleRebuild() {
    if (this.rebuildQueued) return
    this.rebuildQueued = true
    queueMicrotask(() => {
      this.rebuildQueued = false
      if (this.element.isConnected) this._rebuild()
    })
  }

  _rebuild() {
    this._destroy()

    const items = this.triggerTargets.flatMap((triggerEl) => {
      const panelId = triggerEl.getAttribute("aria-controls")
      const targetEl = document.getElementById(panelId)
      if (!targetEl) return [] // not rendered yet; its panel target will bring us back
      return [
        {
          id: `#${panelId}`,
          triggerEl,
          targetEl,
          iconEl: triggerEl.querySelector("[data-accordion-icon]"),
          active: triggerEl.getAttribute("aria-expanded") === "true",
        },
      ]
    })
    if (items.length === 0) return

    this.accordion = new Accordion(this.element, items, this._options(), {
      id: this.element.id || `accordion-${++sequence}`,
      override: true,
    })

    // Flowbite's init() only opens the active items; close the rest so a
    // collapsed trigger's panel, classes and icon match it too.
    items
      .filter((item) => !item.active)
      .forEach((item) => this.accordion.close(item.id))
  }

  _destroy() {
    this.accordion?.destroyAndRemoveInstance()
    this.accordion = null
  }

  // Only pass class options that are declared; an undefined value would
  // override Flowbite's defaults and crash its open()/close().
  _options() {
    const { activeClassesValue: active, inactiveClassesValue: inactive } = this
    return {
      alwaysOpen: this.alwaysOpenValue,
      ...(active && { activeClasses: active }),
      ...(inactive && { inactiveClasses: inactive }),
    }
  }
}
